import config from "../../config";

// ─── Core HTTP helpers ────────────────────────────────────────────────────────
// Identical boilerplate to sales.service.js and purchase.service.js so the
// Finance module has zero runtime dependency on either of those files.

const getAccountId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.accountId;
};

const buildUrl = (path, params = {}) => {
    const filtered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    const qs = new URLSearchParams({ account_id: getAccountId(), ...filtered });
    return `${config.apiUrl}/${path}?${qs}`;
};

const parseErrorMessage = async (res, fallback) => {
    let msg = fallback;
    try {
        const j = await res.json();
        msg = j?.error || j?.message || msg;
    } catch { }
    return msg;
};

const postJson = async (path, body) => {
    const res = await fetch(`${config.apiUrl}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...body }),
    });
    if (!res.ok) {
        const msg = await parseErrorMessage(res, `HTTP ${res.status}`);
        throw new Error(msg);
    }
    return res.json();
};

const putJson = async (path, body) => {
    const res = await fetch(`${config.apiUrl}/${path}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...body }),
    });
    if (!res.ok) {
        const msg = await parseErrorMessage(res, `HTTP ${res.status}`);
        throw new Error(msg);
    }
    return res.json();
};

const del = async (path, params) => {
    const res = await fetch(buildUrl(path, params), { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

const get = async (path, params = {}) => {
    const res = await fetch(buildUrl(path, params));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

/* ════════════════════════════════════════════════════════════════
   SALES CREDIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getCreditNotes = (p = {}) => get("getCreditNotes", p);
export const getCNKpis = () => get("getCNKpis");
export const getCNById = (id) => get("getCNById", { id });
export const createCreditNote = (data) => postJson("createCreditNote", data);
export const updateCreditNote = (id, data) => putJson("updateCreditNote", { id, ...data });
export const deleteCreditNote = (id) => del("deleteCreditNote", { id });

/* ════════════════════════════════════════════════════════════════
   SALES DEBIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getDebitNotes = (p = {}) => get("getDebitNotes", p);
export const getDNKpis = () => get("getDNKpis");
export const getDNById = (id) => get("getDNById", { id });
export const createDebitNote = (data) => postJson("createDebitNote", data);
export const updateDebitNote = (id, data) => putJson("updateDebitNote", { id, ...data });
export const deleteDebitNote = (id) => del("deleteDebitNote", { id });

/* ════════════════════════════════════════════════════════════════
   PURCHASE CREDIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getPurchaseCreditNotes = (p = {}) => get("getPurchaseCreditNotes", p);
export const getPurchaseCreditNoteKpis = () => get("getPurchaseCreditNoteKpis");
export const getPurchaseCreditNoteById = (id) => get("getPurchaseCreditNoteById", { id });
export const createPurchaseCreditNote = (data) => postJson("createPurchaseCreditNote", data);
export const updatePurchaseCreditNote = (id, data) => putJson("updatePurchaseCreditNote", { id, ...data });
export const deletePurchaseCreditNote = (id) => del("deletePurchaseCreditNote", { id });

/* ════════════════════════════════════════════════════════════════
   PURCHASE DEBIT NOTE
   ════════════════════════════════════════════════════════════════ */
export const getPurchaseDebitNotes = (p = {}) => get("getPurchaseDebitNotes", p);
export const getPurchaseDebitNoteKpis = () => get("getPurchaseDebitNoteKpis");
export const getPurchaseDebitNoteById = (id) => get("getPurchaseDebitNoteById", { id });
export const createPurchaseDebitNote = (data) => postJson("createPurchaseDebitNote", data);
export const updatePurchaseDebitNote = (id, data) => putJson("updatePurchaseDebitNote", { id, ...data });
export const deletePurchaseDebitNote = (id) => del("deletePurchaseDebitNote", { id });

/* ════════════════════════════════════════════════════════════════
   PAYMENT VOUCHER
   ════════════════════════════════════════════════════════════════ */
export const getPaymentVouchers = (p = {}) => get("getPaymentVouchers", p);
export const getPaymentVoucherKpis = () => get("getPaymentVoucherKpis");
export const getPaymentVoucherById = (id) => get("getPaymentVoucherById", { id });
export const createPaymentVoucher = (data) => postJson("createPaymentVoucher", data);
export const updatePaymentVoucher = (id, data) => putJson("updatePaymentVoucher", { id, ...data });
export const deletePaymentVoucher = (id) => del("deletePaymentVoucher", { id });
export const approvePaymentVoucher = (id) => putJson("updatePaymentVoucher", { id, status: "Approved" });
export const rejectPaymentVoucher = (id, rejectionReason) => putJson("updatePaymentVoucher", { id, status: "Rejected", rejectionReason });

/* ════════════════════════════════════════════════════════════════
   CURRENCY SETTINGS
   ════════════════════════════════════════════════════════════════ */
export const getCurrencySettings = (accountId) => get("getCurrencySettings", { company: accountId });